import { actionTypes as FirebaseActions } from 'react-redux-firebase';
import { REHYDRATE } from 'redux-persist';

import { CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID } from '../../../constants/bucket';
import { DEFAULT_CREATE_SEND_CONTENTS_STATE } from '../../../constants/createSendContents';
import { isMarketplaceBucketItem, TCreateSendBucketItem } from '../../../types/bucket';
import { ICampaign } from '../../../types/campaigns';
import {
  IChangeContentsItemValuePayload,
  ICreateSendContentsState,
  ISetContentsActiveTabPayload,
  ISetContentsItemSkuOptionsPayload,
} from '../../../types/createSendContents';
import { CreateSendMethodEnum } from '../../../types/createSendFlow';
import { IGiftBudget, isMarketplaceSourced } from '../../../types/marketplace';
import { isItemDigital, isItemOnDemand } from '../../../utils/inventories';
import * as AuthActions from '../../actions/auth';
import * as ContentsActions from '../actions/contents';
import * as MethodActions from '../actions/method';

export const initialState: ICreateSendContentsState = DEFAULT_CREATE_SEND_CONTENTS_STATE;

const matchesId = (item: TCreateSendBucketItem, id: string) =>
  item.item_id ? item.item_id === id : !isMarketplaceBucketItem(item) && item.product_sku === id;

/**
 * `pygOptionOrder` ranks kits and picks by id, so any id whose owner is gone must go with it:
 * otherwise a removed-then-re-added option resurrects its old rank instead of appending, and
 * the persisted array grows without bound.
 */
const prunePygOptionOrder = (order: string[], kits: ICampaign[] | null, items: TCreateSendBucketItem[]): string[] => {
  if (!order.length) {
    return order;
  }

  const live = new Set<string>([...(kits ?? []).map(({ box_id }) => box_id), ...items.map(({ item_id }) => item_id)]);
  const pruned = order.filter((id) => live.has(id));

  return pruned.length === order.length ? order : pruned;
};

const appendPygOption = (
  order: string[],
  kits: ICampaign[] | null,
  items: TCreateSendBucketItem[],
  addedId: string,
): string[] => {
  const pruned = prunePygOptionOrder(order, kits, items);
  const ranked = new Set(pruned);
  const backfilled = [
    ...(kits ?? []).map(({ box_id }) => box_id),
    ...items.filter(isMarketplaceSourced).map(({ item_id }) => item_id),
  ].filter((id) => id !== addedId && !ranked.has(id));

  return ranked.has(addedId) ? [...pruned, ...backfilled] : [...pruned, ...backfilled, addedId];
};

/**
 * Location has no claim step, so a marketplace pick or a recipient budget renders nowhere there
 * — yet left in state it still satisfies the contents gate. Both the in-session method switch
 * and a rehydrated Location draft have to drop them.
 */
const dropContentLocationCannotShow = (state: ICreateSendContentsState): ICreateSendContentsState => {
  const items = state.items.filter((item) => !isMarketplaceSourced(item));

  if (items.length === state.items.length && !state.giftBudget && !state.isGiftBudgetPending) {
    return state;
  }

  return {
    ...state,
    items,
    giftBudget: null,
    isGiftBudgetPending: false,
    pygOptionOrder: prunePygOptionOrder(state.pygOptionOrder, state.kits, items),
  };
};

const reducer = (state: ICreateSendContentsState = initialState, action: any): ICreateSendContentsState => {
  switch (action.type) {
    case AuthActions.ADMIN_ROLE_CHANGED:
    case AuthActions.CHANGE_ORGANIZATION_SUCCESS:
    case FirebaseActions.LOGOUT: {
      return initialState;
    }
    case MethodActions.SET_CS_METHOD: {
      return action.payload === CreateSendMethodEnum.Location ? dropContentLocationCannotShow(state) : state;
    }
    // A draft saved on Location fires no method switch on reload, so the same rule applies here.
    case REHYDRATE: {
      const rehydrated = action.payload?.contents as ICreateSendContentsState | undefined;

      if (!rehydrated || action.payload?.method !== CreateSendMethodEnum.Location) {
        return state;
      }

      return dropContentLocationCannotShow({ ...state, ...rehydrated });
    }
    case ContentsActions.SET_CS_CONTENTS_ACTIVE_TAB: {
      const { group, tab } = action.payload as ISetContentsActiveTabPayload;

      return { ...state, activeTab: { ...state.activeTab, [group]: tab } };
    }
    case ContentsActions.TOGGLE_CS_KIT: {
      const picked = action.payload as ICampaign;
      const current = state.kits;
      const isRemoving = Boolean(current?.some(({ box_id }) => box_id === picked.box_id));

      const kits = isRemoving
        ? (current ?? []).filter(({ box_id }) => box_id !== picked.box_id)
        : [...(current ?? []), picked];

      // Kits and marketplace picks mix freely here; only the custom kit is single-source.
      // So a kit toggle clears just the leftover IE custom-cart items.
      const items = state.items.filter(isMarketplaceSourced);

      // The sentinel must go: keeping it forces contentType to 'custom', hiding the kits.
      return {
        ...state,
        kits,
        boxId: '',
        items,
        pygOptionOrder: isRemoving
          ? prunePygOptionOrder(state.pygOptionOrder, kits, items)
          : appendPygOption(state.pygOptionOrder, kits, items, picked.box_id),
      };
    }
    case ContentsActions.SET_CS_KITS: {
      const kits = action.payload as ICampaign[] | null;

      return { ...state, kits, pygOptionOrder: prunePygOptionOrder(state.pygOptionOrder, kits, state.items) };
    }
    case ContentsActions.SET_CS_PYG_OPTION_ORDER: {
      return { ...state, pygOptionOrder: action.payload as string[] };
    }
    case ContentsActions.SET_CS_GIFT_CHOICE_MODE: {
      return { ...state, kits: action.payload ? [] : null };
    }
    case ContentsActions.RESET_CS_CUSTOM_KIT: {
      return {
        ...state,
        items: [],
        boxId: '',
        pygOptionOrder: prunePygOptionOrder(state.pygOptionOrder, state.kits, []),
      };
    }
    case ContentsActions.APPLY_CS_CUSTOM_KIT: {
      return {
        ...state,
        kits: null,
        items: action.payload as TCreateSendBucketItem[],
        boxId: CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID,
      };
    }
    case ContentsActions.SET_CS_BOX_ID: {
      return { ...state, boxId: action.payload as string };
    }
    case ContentsActions.ADD_CS_ITEM: {
      const itemToAdd = action.payload as TCreateSendBucketItem;
      // Marketplace picks are always matched by `item_id` — they carry no `product_sku`.
      const existing =
        !isMarketplaceBucketItem(itemToAdd) && isItemOnDemand(itemToAdd)
          ? state.items.find((item) => !isMarketplaceBucketItem(item) && item.product_sku === itemToAdd.product_sku)
          : state.items.find((item) => item.item_id === itemToAdd.item_id);

      if (!existing || (!isMarketplaceBucketItem(existing) && isItemDigital(existing))) {
        const items = [...state.items, itemToAdd];

        return {
          ...state,
          items,
          pygOptionOrder: isMarketplaceSourced(itemToAdd)
            ? appendPygOption(state.pygOptionOrder, state.kits, items, itemToAdd.item_id)
            : state.pygOptionOrder,
        };
      }

      return {
        ...state,
        items: state.items.map((item) =>
          item === existing ? { ...item, quantity: (item.quantity ?? 0) + (itemToAdd.quantity ?? 0) } : item,
        ),
      };
    }
    case ContentsActions.REMOVE_CS_ITEM: {
      const items = state.items.filter((item) => !matchesId(item, action.payload));

      return { ...state, items, pygOptionOrder: prunePygOptionOrder(state.pygOptionOrder, state.kits, items) };
    }
    case ContentsActions.SET_CS_GIFT_BUDGET: {
      const budget = action.payload as IGiftBudget | null;

      return {
        ...state,
        giftBudget: budget,
        // A committed budget is never also pending.
        isGiftBudgetPending: budget === null && Boolean(action.meta?.isPending),
      };
    }
    case ContentsActions.SET_CS_ITEM_SKU_OPTIONS: {
      const { itemId, skuOptions, supplier } = action.payload as ISetContentsItemSkuOptionsPayload;
      const details = supplier === undefined ? { sku_options: skuOptions } : { sku_options: skuOptions, supplier };

      // Inventory MSKU options are sender-editable quantities; a catalog read must not clobber them.
      return {
        ...state,
        items: state.items.map((item) =>
          isMarketplaceBucketItem(item) && item.item_id === itemId ? { ...item, ...details } : item,
        ),
      };
    }
    case ContentsActions.CHANGE_CS_ITEM_VALUE: {
      const { id, name, value } = action.payload as IChangeContentsItemValuePayload;

      if (!state.items.some((item) => matchesId(item, id))) {
        return state;
      }

      return {
        ...state,
        items: state.items.map((item) => (matchesId(item, id) ? { ...item, [name]: value } : item)),
      };
    }
    default:
      return state;
  }
};

export default reducer;
